<template>
  <div class="tir-pm-text-editor-file-preview">
    <div
      class="tir-pm-text-editor-file-preview__top-line tir-pm-text-editor-file-preview-top-line"
    >
      <span class="tir-pm-text-editor-file-preview-top-line__name-sign">
        {{ props.label }}
      </span>
      <div
        v-if="editing"
        class="tir-pm-text-editor-file-preview-top-line__top-settings"
      >
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          title="К редактированию"
          @click="onClickToEdit"
        >
          <template #leftSlot><PencilSquareIcon></PencilSquareIcon></template>
        </TirPmButton>
      </div>
    </div>
    <div class="tir-pm-text-editor-file-preview-content">
      <div
        class="tir-pm-text-editor-file-preview-content__container"
        :style="{ height: heightStyle }"
      >
        <div
          class="tir-pm-text-editor-file-preview-content__preview"
          ref="$content"
          v-html="innerContent"
        ></div>
        <Teleport
          class="tir-pm-text-editor-file-preview-content__widget"
          v-if="isRenderTeleport"
          v-for="widget in widgets"
          :key="widget.DataId"
          :to="`.tir-pm-text-editor-file-preview-content__preview [data-widget-id='${widget.DataId}']`"
          ><component
            :is="widget.Component"
            v-bind="widget.Props"
            v-on="widget.Listeners"
        /></Teleport>
      </div>

      <TirPmButton
        :variant="TirPmButtonVariantEnum.SecondaryState"
        :size="TirPmButtonSizeEnum.Small"
        style="width: 100%"
        class="tir-pm-text-editor-file-preview-content__expand"
        @click="onChangeExpand"
        v-if="isShowExpandContentButton"
      >
        <template #leftSlot
          ><ArrowDownIcon
            class="tir-pm-text-editor-file-preview-content__expand-arrow"
            :class="{
              'tir-pm-text-editor-file-preview-content__expand-arrow--expand':
                isExpandContent,
            }"
        /></template>
        <span v-if="!isExpandContent">Показать все</span>
        <span v-else>Свернуть</span>
      </TirPmButton>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends ITextEditorFile">
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from "tir-pm-button";
import {
  PencilSquareIcon,
  ArrowDownIcon,
} from "tir-style-system/icons/outline";
import { nextTick, watch } from "vue";
import { computed, onMounted, ref } from "vue";
import { PropType } from "vue";
import { ITextEditorFile } from "./interfaces";
import { useEditorWidgets } from "./composables";

/**События компонента */
const emit = defineEmits<{
  (e: "toEdit"): void;
  (e: "changeExpand"): void;
  /**Событие клика по кнопке удаления файла*/
  (e: "onClickDeleteFile", fileId: T["FileId"]): void;
  /**Событие клика по кнопке редактирования файла */
  (e: "onClickEditFile", fileId: T["FileId"]): void;
  /**Событие сохранения названия файла */
  (e: "saveFileName", fileId: T["FileId"], fileName: string): void;
}>();

const props = defineProps({
  /**Возможность редактировать */
  editing: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**Текст поля ввода */
  content: { type: String as PropType<string>, default: "" },
  /**Лейбл над основным инпутом редактора */
  label: { default: "", type: String as PropType<string> },
  /* Высота для редактора */
  height: { type: String as PropType<string>, default: "321" },
  /* Раскрыто ли полностью описание */
  isExpand: { type: Boolean as PropType<boolean>, default: false },
  /**Все файлы */
  allFiles: { type: Array as PropType<Array<T>>, default: [] },
  /**Загрузка при редактировании названия */
  filesLoadingEditName: {
    type: Array as PropType<Array<T["FileId"]>>,
    default: [],
  },
});

/** Индикатор expand-кнопки */
const isExpandContent = computed(() => props.isExpand);

/**Отрендерить ли телепорты */
const isRenderTeleport = ref(false);
/**Внутреннее состояние контента */
const innerContent = ref(props.content);
/**Обновить внутренне состояние контента */
const updateInnerContent = async (value: string) => {
  isRenderTeleport.value = false;
  innerContent.value = value;
  await nextTick();
  isRenderTeleport.value = true;
};
watch(
  () => props.content,
  () => updateInnerContent(props.content)
);
watch(
  () => isExpandContent.value,
  () => {
    calculateInnerHeightContent();
  }
);

/**Список виджетов */
const { widgets } = useEditorWidgets({
  content: computed(() => props.content),
  files: computed(() => props.allFiles),
  filesLoadingEditName: computed(() => props.filesLoadingEditName),
  emit: emit,
});

/** Изменить состояние кнопки раскрытия */
const onChangeExpand = () => {
  emit("changeExpand");
};

const $content = ref<HTMLElement>(null);

/** Нажатие кнопки "К редактированию" */
const onClickToEdit = () => {
  emit("toEdit");
};

/** Индикатор expand-кнопки */
const isShowExpandContentButton = ref(false);

const computedPropsContent = computed(() => props.content);
const computedPropsHeight = computed(() => props.height);

const innerHeightContent = ref(0);
const heightStyle = ref(+computedPropsHeight.value / 16 + "rem");

/**Отобразить анимацию и потом раскрыть описание */

watch(
  () => isExpandContent.value,
  (newValue) => {
    if (newValue) {
      heightStyle.value = `${innerHeightContent.value / 16 + 1}rem`;
      setTimeout(() => (heightStyle.value = `max-content`), 100);
    } else {
      heightStyle.value = `${innerHeightContent.value / 16 + 1}rem`;
      setTimeout(() => {
        heightStyle.value = +computedPropsHeight.value / 16 + "rem";
      });
    }
  },
  { immediate: true }
);

watch(computedPropsContent, () => {
  calculateInnerHeightContent();
});

onMounted(() => {
  isRenderTeleport.value = true;
  calculateInnerHeightContent();
});

function calculateInnerHeightContent() {
  setTimeout(() => {
    innerHeightContent.value = $content.value.offsetHeight;

    isShowExpandContentButton.value =
      innerHeightContent.value > +computedPropsHeight.value;
  });
}
</script>
